import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { jobsApi } from '../../api/jobs'
import { voiceApi } from '../../api/voice'
import { useJobLive } from '../../hooks/useJobLive'
import { useSpoke } from '../../hooks/useSpoke'
import { formatRelative } from '../../lib/format'
import { jobKindLabel, schedulerRunId } from '../../lib/jobs'
import { scenarioLabel } from '../../lib/voice'
import type { LiveJob, VoiceShape } from '../../types/jobs'
import type { LeadResearchShape } from '../../types/leads'
import type { OnboardShape } from '../../types/onboarding'
import type { Job, JobStep } from '../../types/voice'
import { Pipeline } from './Pipeline'

// The job screen: what Scout is doing to your mail right now, how fast, how long is left, and the
// profile taking shape underneath it. Live over the jobs hub; polling underneath so it never lies.
// Voice does not exist until this lands, so the only door to it is the one at the bottom.

const IMPORT_STEPS = new Set(['voice-sync'])

const STEP_COPY: Record<string, { title: string; doing: string; unit: string }> = {
  'voice-sync': { title: 'Reading your sent mail', doing: 'Reading', unit: 'messages' },
  'voice-index': { title: 'Measuring your style', doing: 'Measuring', unit: 'messages' },
  'voice-scenarios': { title: 'Sorting by audience and intent', doing: 'Sorting', unit: 'messages' },
  'voice-exemplars': { title: 'Choosing your best examples', doing: 'Choosing', unit: 'scenarios' },
  'voice-describe': { title: 'Writing your descriptor', doing: 'Describing', unit: 'scenarios' },
  'voice-verify': { title: 'Checking it sounds like you', doing: 'Checking', unit: 'scenarios' },
  'scan-messages': { title: 'Sorting your mail with a model', doing: 'Sorting', unit: 'messages' },
  'scan-rebuild': { title: 'Rebuilding the profile over the new scenarios', doing: 'Rebuilding', unit: 'steps' },
  'measure-drafts': { title: 'Drafting held-out messages three ways', doing: 'Measuring', unit: 'messages' },
  'deal-threads': { title: 'Reading your threads for leads and opportunities', doing: 'Reading', unit: 'threads' },
  'deal-attachments': { title: 'Fetching the proposals and SOWs you sent', doing: 'Fetching', unit: 'pursuits' },
  'contact-weigh': { title: 'Weighing every relationship from your mail', doing: 'Weighing', unit: 'contacts' },
  'contact-flag': { title: 'Flagging dormant ties and likely moves', doing: 'Flagging', unit: 'contacts' },
  'contact-locate': { title: 'Reading where each contact writes from', doing: 'Reading', unit: 'contacts' },
  'methodology-ingest': { title: 'Reading the methodology and laying out its tactics', doing: 'Reading', unit: 'steps' },
  'transcript-import': { title: 'Pulling meeting transcripts from the ledger', doing: 'Pulling', unit: 'transcripts' },
  'extract-requirements': { title: 'Reading the transcript for requirements', doing: 'Reading', unit: 'transcripts' },
  'extract-takeaways': { title: 'Reading the transcript for takeaways and promises', doing: 'Reading', unit: 'transcripts' },
  'ledger-land': { title: 'Tying the ledger to the meeting’s account', doing: 'Tying', unit: 'transcripts' },
  'calendar-read': { title: 'Reading your calendar', doing: 'Reading', unit: 'events' },
  'calendar-resolve': { title: 'Resolving who is in the room and which account', doing: 'Resolving', unit: 'meetings' },
  'calendar-link': { title: 'Tying transcripts to their meetings', doing: 'Tying', unit: 'transcripts' },
  'pull-messages': { title: 'Pulling messages and attachments', doing: 'Pulling', unit: 'messages' },
  'research-gather': { title: 'Gathering what is on file about the company', doing: 'Gathering', unit: 'sources' },
  'research-write': { title: 'Writing the research note from what is on file', doing: 'Writing', unit: 'steps' },
  'research-person-gather': { title: 'Gathering what is on file about the person', doing: 'Gathering', unit: 'sources' },
  'research-person-write': { title: 'Writing the brief from what is on file', doing: 'Writing', unit: 'steps' },
  'prepare-rank': { title: 'Working out who is at the top of your list', doing: 'Ranking', unit: 'people' },
  'prepare-research': { title: 'Writing a brief for each of them', doing: 'Briefing', unit: 'people' },
  'weekly-write': { title: 'Writing the week from the facts on file', doing: 'Writing', unit: 'steps' },
  'photo-gravatar': { title: 'Looking each contact up on Gravatar', doing: 'Looking up', unit: 'contacts' },
  'photo-land': { title: 'Landing the photos and tallying the coverage', doing: 'Landing', unit: 'photos' },
  'threat-registry': { title: 'Reading the register of rivals', doing: 'Reading', unit: 'companies' },
  'threat-meetings': { title: 'Looking for rivals in your meetings', doing: 'Reading', unit: 'meetings' },
  'threat-threads': { title: 'Looking for rivals in your threads', doing: 'Reading', unit: 'threads' },
  'threat-land': { title: 'Flagging the threats and taking them out of the pipeline', doing: 'Flagging', unit: 'threats' },
  'onboard-parse': { title: 'Reading the file and working out the columns', doing: 'Reading', unit: 'rows' },
  'onboard-clean': { title: 'Cleaning the rows and folding the duplicates together', doing: 'Cleaning', unit: 'contacts' },
  'onboard-match': { title: 'Matching them against what Scout already knows', doing: 'Matching', unit: 'contacts' },
  'onboard-flag': { title: 'Flagging the contacts you already have a relationship with', doing: 'Flagging', unit: 'contacts' },
  'improve-gather': { title: 'Reading what you corrected', doing: 'Reading', unit: 'corrections' },
  'improve-propose': { title: 'Working out the rule that would have caught it', doing: 'Proposing', unit: 'corrections' },
  'improve-verify': { title: 'Replaying each candidate against everything on file', doing: 'Verifying', unit: 'candidates' },
  'improve-apply': { title: 'Applying what held, keeping what did not', doing: 'Applying', unit: 'rules' },
  'sync-messages': { title: 'Checking your mailbox for new messages', doing: 'Checking', unit: 'messages' },
}

export function JobPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const spoke = useSpoke()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { state, error } = useJobLive(spoke, jobId)
  const landed = state ? isTerminal(state.job) : false
  const resume = useMutation({ mutationFn: () => voiceApi.build(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })

  useEffect(() => {
    if (!landed) return
    void qc.invalidateQueries({ queryKey: ['landscape', spoke] })
    void qc.invalidateQueries({ queryKey: ['voice-profile', spoke] })
    void qc.invalidateQueries({ queryKey: ['voice-history', spoke] })
    void qc.invalidateQueries({ queryKey: ['mailbox', spoke] })
  }, [landed, qc, spoke])

  if (error && !state) {
    return (
      <section className="page job">
        <p className="kicker"><Link to="/landscape">Digital Landscape</Link></p>
        <h1>That job is not yours to see</h1>
        <div className="refusal"><strong>No such job</strong><p>{error.message}</p></div>
      </section>
    )
  }
  if (!state) return <p className="muted centered">Loading…</p>

  const { job, shape } = state
  const current = job.steps.find((s) => s.status === 'Running') ?? null
  const overall = overallProgress(job)
  const importing = current ? IMPORT_STEPS.has(current.stepId) : job.steps.some((s) => IMPORT_STEPS.has(s.stepId) && s.status === 'Pending')
  const gaps = job.warnings > 0
  const stoppedAt = job.status === 'Failed' ? job.steps.find((s) => s.status === 'Failed') ?? null : null
  const scanning = job.kind === 'voice-scan'
  const dealing = job.kind === 'deal-scan'
  const syncing = job.kind === 'calendar-sync'
  const ledgering = job.kind === 'transcript-extract'
  const ledgerTranscript = ledgering ? transcriptOf(job) : null
  const researching = job.kind === 'lead-research'
  const researchShape = researching && state.job.shapeJson ? parseResearchShape(state.job.shapeJson) : null
  const writing = job.kind === 'weekly-write'
  const weeklyShape = writing && job.shapeJson ? parseWeeklyShape(job.shapeJson) : null
  const photographing = job.kind === 'photo-enrich'
  const photoShape = photographing && job.shapeJson ? parsePhotoShape(job.shapeJson) : null
  const watching = job.kind === 'threat-watch'
  const threatShape = watching && job.shapeJson ? parseThreatShape(job.shapeJson) : null
  // his priority-contacts workbook is staged the same way and shows the same shape panel
  const onboarding = job.kind === 'contact-onboard' || job.kind === 'priority-contacts'
  const onboardShape = onboarding && job.shapeJson ? parseOnboardShape(job.shapeJson) : null
  // The two kinds the self-improvement workstream added: the nightly loop, and the live mail check that
  // runs between nightly ticks. Both are short, so the console says what they were for rather than "Done".
  const improving = job.kind === 'self-improve'
  const syncingMail = job.kind === 'mail-sync'
  const noting = job.kind === 'notes-sync'
  const title = improving
    ? job.status === 'Completed' ? (gaps ? 'Learned what it could; the rest is back with you' : 'Scout learned from your corrections')
      : job.status === 'Failed' ? 'The improvement run stopped'
      : job.status === 'Cancelled' ? 'Improvement run cancelled'
      : 'Learning from your corrections'
    : syncingMail
    ? job.status === 'Completed' ? 'Your mailbox is up to date'
      : job.status === 'Failed' ? 'The mail check stopped'
      : job.status === 'Cancelled' ? 'Mail check cancelled'
      : 'Checking for new mail'
    : noting
    ? job.status === 'Completed' ? (gaps ? 'Your OneNote is read, with gaps' : 'Your OneNote is on file')
      : job.status === 'Failed' ? 'The OneNote read stopped'
      : job.status === 'Cancelled' ? 'OneNote read cancelled'
      : 'Reading your OneNote'
    : job.status === 'Completed' ? (onboarding ? ((onboardShape?.flagged ?? 0) > 0 ? 'Staged, and some of them know you' : 'Staged and waiting on you') : watching ? ((threatShape?.threats ?? 0) > 0 ? 'Rivals are on the board' : 'No rival is at your accounts') : writing ? (gaps ? 'The week is laid out, without a model' : 'The week is written') : photographing ? (gaps ? 'Photos found, some lookups unanswered' : 'Photos are on the contacts') : gaps ? 'Built, with gaps' : scanning ? 'Your mail is sorted' : dealing ? 'Your pipeline is on the map' : syncing ? 'Your calendar is on the map' : ledgering ? 'The ledger is on the page' : researching ? 'The research note is on file' : job.kind === 'voice-build' ? 'Your voice profile is ready' : 'Done')
    : job.status === 'Failed' ? (researching ? 'The research stopped' : writing ? 'The write stopped' : photographing ? 'The photo lookup stopped' : 'The import stopped')
    : job.status === 'Cancelled' ? 'Import cancelled'
    : onboarding ? 'Staging the contact import' : watching ? 'Watching for rivals' : scanning ? 'Sorting your mail' : dealing ? 'Reading your threads' : syncing ? 'Reading your calendar' : ledgering ? 'Reading the transcript' : researching ? `Researching ${researchShape?.companyName ?? 'the company'}` : writing ? 'Writing the week' : photographing ? 'Finding profile pictures' : job.kind === 'voice-build' ? (importing ? 'Reading your mailbox' : 'Shaping your voice profile') : 'Working'

  return (
    <section className="page job">
      <p className="kicker">
        <Link to="/landscape">Digital Landscape</Link> · step 2 of 5 · <Channel state={state} />
        {schedulerRunId(job.requestJson) && <span className="tag scheduled" title="Started by the automatic scan, not by hand">scheduled</span>}
      </p>
      <h1>{title}</h1>

      <div className={`console ${job.status.toLowerCase()}${gaps && job.status === 'Completed' ? ' degraded' : ''}`}>
        <div className="console-ring">
          <Ring fraction={overall} tone={job.status === 'Completed' && gaps ? 'degraded' : job.status.toLowerCase()} />
          <span className="console-ring-label">{job.status === 'Completed' ? (gaps ? 'with gaps' : 'done') : job.status === 'Failed' ? 'stopped' : job.status === 'Cancelled' ? 'cancelled' : 'overall'}</span>
        </div>
        <div className="console-readout">
          <Readout label={current ? STEP_COPY[current.stepId]?.doing ?? 'Working' : job.status === 'Pending' ? 'Queued' : 'Finished'} value={current ? `${current.done.toLocaleString()}${current.total ? ` / ${current.total.toLocaleString()}` : ''}` : job.status === 'Pending' ? 'waiting for a worker' : job.steps.filter((s) => s.status === 'Completed').length + ' of ' + job.steps.length + ' steps'} unit={current ? STEP_COPY[current.stepId]?.unit ?? '' : ''} />
          <Readout label="Speed" value={current?.rate ? rate(current.rate) : job.status === 'Running' ? 'measuring' : '—'} unit={current?.rate ? `${STEP_COPY[current.stepId]?.unit ?? 'items'}/s` : ''} />
          <Readout label="Remaining" value={eta(current, job)} unit={current && current.etaSeconds != null && !isTerminal(job) ? 'in this step' : ''} />
          <Readout label="Elapsed" value={<Elapsed job={job} />} unit="" />
        </div>
        <div className="console-scene">
          <Pipeline job={job} current={current} shape={shape} />
        </div>
      </div>

      <div className="job-grid">
        <div className="card steps-card">
          <h2>What Scout is doing</h2>
          <ol className="job-steps">
            {job.steps.map((s, i) => <StepRow key={s.stepId} step={s} index={i} phase={IMPORT_STEPS.has(s.stepId) ? (i === 0 ? 'Import' : null) : i === 1 ? 'Post-import processing' : null} />)}
          </ol>
          {job.error && <div className="refusal"><strong>Stopped{stoppedAt ? ` while ${STEP_COPY[stoppedAt.stepId]?.title.toLowerCase() ?? stoppedAt.label.toLowerCase()}` : ''}</strong><p>{job.error}</p><p className="muted small">Resume picks up where it left off; nothing already read is read twice.</p></div>}
          {job.status === 'Completed' && gaps && (
            <div className="gaps">
              <strong>What did not happen</strong>
              <ul>
                {job.steps.filter((s) => s.warnings > 0).map((s) => <li key={s.stepId}><span>{STEP_COPY[s.stepId]?.title ?? s.label}</span>{s.message}</li>)}
                {[...new Set(state.log.filter((l) => l.level === 'warn').map((l) => l.message.replace(/^[^:]+: /, '')))].slice(0, 3).map((m) => <li key={m} className="why">{m}</li>)}
              </ul>
              <p className="muted small">Each of these is one thing this read could not do; everything else it landed stands. The next build tries them again.</p>
            </div>
          )}
          {job.status === 'Cancelled' && <p className="muted">You disconnected the mailbox while it was being read. Nothing was kept.</p>}
        </div>

        <div className="card shape-card">
          <h2>{onboarding ? 'The import, taking shape' : watching ? 'What the watch is finding' : scanning || dealing || ledgering ? 'What the model is finding' : syncing ? 'Your meetings, taking shape' : researching ? 'What the note stands on' : writing ? 'The update, taking shape' : photographing ? 'Faces, taking shape' : 'Your voice, taking shape'}</h2>
          {onboarding ? (
            onboardShape ? <OnboardPanel shape={onboardShape} /> : <p className="muted">The first rows land here as they are read.</p>
          ) : watching ? (
            threatShape ? <ThreatPanel shape={threatShape} /> : <p className="muted">The register of rivals is being read.</p>
          ) : photographing ? (
            photoShape ? <PhotoPanel shape={photoShape} /> : <p className="muted">The first lookups land here.</p>
          ) : ledgering ? (
            state.job.shapeJson && isLedgerShape(JSON.parse(state.job.shapeJson)) ? <LedgerPanel shape={JSON.parse(state.job.shapeJson) as LedgerShape} /> : <p className="muted">The first transcript lands here as it is read.</p>
          ) : researching ? (
            researchShape ? <ResearchPanel shape={researchShape} /> : <p className="muted">The evidence on file lands here first.</p>
          ) : writing ? (
            weeklyShape ? <WeeklyPanel shape={weeklyShape} /> : <p className="muted">The facts are being gathered.</p>
          ) : syncing ? (
            state.job.shapeJson && isCalendarShape(JSON.parse(state.job.shapeJson)) ? <CalendarPanel shape={JSON.parse(state.job.shapeJson) as CalendarShape} /> : <p className="muted">The first events land here as they are read.</p>
          ) : dealing ? (
            state.job.shapeJson && isDealShape(JSON.parse(state.job.shapeJson)) ? <DealPanel shape={JSON.parse(state.job.shapeJson) as DealShape} /> : <p className="muted">The first batch of threads lands here.</p>
          ) : scanning ? (
            state.job.shapeJson && isScanShape(JSON.parse(state.job.shapeJson)) ? <ScanPanel shape={JSON.parse(state.job.shapeJson) as ScanShape} /> : <p className="muted">The first batch lands here.</p>
          ) : !shape || shape.messagesKept === 0 ? (
            <p className="muted">The first messages will land here as they are read.</p>
          ) : (
            <Shape shape={shape} />
          )}
        </div>
      </div>

      <LiveLog state={state} />

      <div className="job-foot">
        {job.status === 'Completed' && (
          <>
            <p>{completionLine(job, shape, gaps)}</p>
            <span className="actions">
              {onboarding ? <button className="button primary large" onClick={() => navigate(onboardShape?.batchId ? `/onboarding/${encodeURIComponent(onboardShape.batchId)}` : '/onboarding')}>Review the import</button>
                : watching ? <button className="button primary large" onClick={() => navigate('/meetings')}>Open my meetings</button>
                : dealing ? <button className="button primary large" onClick={() => navigate('/opportunities')}>Open my opportunities</button>
                : syncing ? <button className="button primary large" onClick={() => navigate('/meetings')}>Open my meetings</button>
                : ledgering ? <button className="button primary large" onClick={() => navigate(ledgerTranscript ? `/transcripts/${encodeURIComponent(ledgerTranscript)}` : '/transcripts')}>{ledgerTranscript ? 'Open the transcript' : 'Open the transcripts'}</button>
                : researching ? <button className="button primary large" onClick={() => navigate(researchShape?.engagementId ? `/leads/${encodeURIComponent(researchShape.engagementId)}` : '/leads')}>Open the lead</button>
                : writing ? <button className="button primary large" onClick={() => navigate(weeklyShape?.weekStart ? `/weekly?week=${encodeURIComponent(weeklyShape.weekStart)}` : '/weekly')}>Open the weekly update</button>
                : photographing ? <button className="button primary large" onClick={() => navigate('/contacts')}>Open my contacts</button>
                : <button className="button primary large" onClick={() => navigate('/voice')}>Open my voice profile</button>}
              {!syncing && !ledgering && !researching && !writing && !photographing && !watching && !onboarding && <button className="button" onClick={() => resume.mutate()} disabled={resume.isPending}>{resume.isPending ? 'Starting…' : 'Read it again'}</button>}
              {!writing && !watching && !onboarding && <Link className="button" to={syncing ? '/meetings' : ledgering ? '/transcripts' : researching ? '/leads' : photographing ? '/accounts' : '/mailbox?manage=1'}>{syncing ? 'Back to meetings' : ledgering ? 'Back to transcripts' : researching ? 'Back to leads' : photographing ? 'Back to accounts' : 'Manage mailbox'}</Link>}
              {watching && <Link className="button" to="/weekly">Threats this week</Link>}
            </span>
            {resume.isError && <span className="error">{resume.error.message}</span>}
          </>
        )}
        {job.status === 'Failed' && researching && (
          <>
            <p>The note was not written. What is on file is unchanged; press Research again from the lead.</p>
            <span className="actions">
              <Link className="button primary large" to={researchShape?.engagementId ? `/leads/${encodeURIComponent(researchShape.engagementId)}` : '/leads'}>Back to the lead</Link>
            </span>
          </>
        )}
        {job.status === 'Failed' && writing && (
          <>
            <p>The write stopped before it finished. Nothing on file changed; write the week again from the update screen.</p>
            <span className="actions"><Link className="button primary large" to="/weekly">Back to the weekly update</Link></span>
          </>
        )}
        {job.status === 'Failed' && photographing && (
          <>
            <p>The lookup stopped before it finished. Every photo already found is kept; press Find photos again to continue.</p>
            <span className="actions"><Link className="button primary large" to="/contacts">Back to contacts</Link></span>
          </>
        )}
        {job.status === 'Failed' && !researching && !writing && !photographing && (
          <>
            <p>The read stopped before it finished. Nothing is lost; resume continues from the last completed read.</p>
            <span className="actions">
              <button className="button primary large" onClick={() => resume.mutate()} disabled={resume.isPending}>{resume.isPending ? 'Resuming…' : 'Resume the read'}</button>
              <Link className="button" to="/mailbox?manage=1">Manage mailbox</Link>
            </span>
            {resume.isError && <span className="error">{resume.error.message}</span>}
          </>
        )}
        {job.status === 'Cancelled' && <Link className="button primary" to="/mailbox?manage=1">Connect a mailbox</Link>}
        {(job.status === 'Running' || job.status === 'Pending') && <p className="muted">You can leave this page; the import keeps going and the map will show it when it lands.</p>}
      </div>

      <RecentJobs currentJobId={job.id} />
    </section>
  )
}

/// Everything Scout has done for this person lately, whichever kind. A job the automatic scan
/// started carries a "scheduled" chip: the chain is theirs, but nobody pressed it.
function RecentJobs({ currentJobId }: { currentJobId: string }) {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['jobs', spoke], queryFn: () => jobsApi.list(spoke, 12), staleTime: 15_000 })
  if (!q.isSuccess || q.data.length <= 1) return null
  return (
    <details className="card recent-jobs">
      <summary><h2>Recent jobs</h2><span className="muted">{q.data.length} in the last while</span></summary>
      <ul className="plain">
        {q.data.map((j) => (
          <li key={j.id} className={j.id === currentJobId ? 'current' : undefined}>
            {j.id === currentJobId ? <strong>{jobKindLabel(j.kind)}</strong> : <Link to={`/jobs/${encodeURIComponent(j.id)}`}>{jobKindLabel(j.kind)}</Link>}
            {schedulerRunId(j.requestJson) && <span className="tag scheduled" title="Started by the automatic scan, not by hand">scheduled</span>}
            <span className="muted small"> · {formatRelative(j.createdAt)} · <span className={`status ${j.status.toLowerCase()}`}>{j.status}</span>{j.warnings > 0 && j.status === 'Completed' ? ', with gaps' : ''}</span>
          </li>
        ))}
      </ul>
    </details>
  )
}

/* ── pieces ── */

function isTerminal(job: Job) {
  return job.status === 'Completed' || job.status === 'Failed' || job.status === 'Cancelled'
}

function overallProgress(job: Job): number {
  if (job.status === 'Completed') return 1
  const n = job.steps.length || 1
  let sum = 0
  for (const s of job.steps) {
    if (s.status === 'Completed' || s.status === 'Skipped') sum += 1
    else if (s.status === 'Running') sum += s.total ? Math.min(1, s.done / s.total) : 0.15
  }
  return Math.min(1, sum / n)
}

function rate(r: number) {
  return r >= 100 ? Math.round(r).toLocaleString() : r >= 10 ? r.toFixed(1) : r.toFixed(2)
}

function eta(current: JobStep | null, job: Job): string {
  if (isTerminal(job)) return '—'
  if (!current) return 'starting'
  if (current.etaSeconds == null) return current.total ? 'measuring' : 'counting'
  return duration(current.etaSeconds)
}

function duration(seconds: number): string {
  if (seconds < 1) return 'under a second'
  if (seconds < 60) return `${Math.ceil(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`
}

function Elapsed({ job }: { job: Job }) {
  const start = job.startedAt ? new Date(job.startedAt).getTime() : null
  const end = job.completedAt ? new Date(job.completedAt).getTime() : null
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (end || !start) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [start, end])
  if (!start) return <>—</>
  return <>{duration(Math.max(0, ((end ?? now) - start) / 1000))}</>
}

function Readout({ label, value, unit }: { label: string; value: React.ReactNode; unit: string }) {
  return (
    <div className="readout">
      <span className="readout-label">{label}</span>
      <span className="readout-value">{value}{unit && <small> {unit}</small>}</span>
    </div>
  )
}

function Ring({ fraction, tone }: { fraction: number; tone: string }) {
  const r = 54
  const c = 2 * Math.PI * r
  return (
    <svg className={`ring ${tone}`} viewBox="0 0 128 128" aria-label={`${Math.round(fraction * 100)} percent`}>
      <circle className="ring-track" cx="64" cy="64" r={r} />
      <circle className="ring-fill" cx="64" cy="64" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - fraction)} />
      <text x="64" y="70" textAnchor="middle" className="ring-text">{Math.round(fraction * 100)}<tspan className="ring-pct">%</tspan></text>
    </svg>
  )
}

function Channel({ state }: { state: LiveJob }) {
  if (isTerminal(state.job)) return <span className="chan done">landed</span>
  if (state.channel === 'live') return <span className="chan live"><i className="dot" />live</span>
  if (state.channel === 'polling') return <span className="chan poll">polling</span>
  return <span className="chan">connecting</span>
}

function StepRow({ step, index, phase }: { step: JobStep; index: number; phase: string | null }) {
  const copy = STEP_COPY[step.stepId] ?? { title: step.label, doing: step.label, unit: '' }
  const frac = step.status === 'Completed' ? 1 : step.status === 'Running' && step.total ? Math.min(1, step.done / step.total) : 0
  const took = step.startedAt && step.completedAt ? (new Date(step.completedAt).getTime() - new Date(step.startedAt).getTime()) / 1000 : null
  return (
    <>
      {phase && <li className="job-phase">{phase}</li>}
      <li className={`job-step ${step.status.toLowerCase()}`}>
        <span className="job-step-no">{step.status === 'Completed' ? <Check /> : step.status === 'Failed' ? '!' : index + 1}</span>
        <div className="job-step-body">
          <div className="job-step-head">
            <strong>{copy.title}</strong>
            <span className="job-step-meta">
              {step.status === 'Running' && step.total ? `${step.done.toLocaleString()} / ${step.total.toLocaleString()} ${copy.unit}` : ''}
              {step.status === 'Running' && !step.total ? `${step.done.toLocaleString()} ${copy.unit}` : ''}
              {step.status === 'Running' && step.rate ? ` · ${rate(step.rate)}/s` : ''}
              {step.status === 'Running' && step.etaSeconds != null && step.total ? ` · ${duration(step.etaSeconds)} left` : ''}
              {step.status === 'Completed' && took != null ? `${duration(took)}` : ''}
              {step.status === 'Pending' ? 'waiting' : ''}
              {step.status === 'Skipped' ? 'skipped' : ''}
            </span>
          </div>
          <div className={`job-bar ${step.status === 'Running' && !step.total ? 'indeterminate' : ''}`}><span style={{ width: `${frac * 100}%` }} /></div>
          {step.message && <p className="job-receipt">{step.message}</p>}
        </div>
      </li>
    </>
  )
}

function Check() {
  return <svg viewBox="0 0 24 24" className="check"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
}

function Shape({ shape }: { shape: VoiceShape }) {
  const maxMonth = Math.max(1, ...shape.months.map((m) => m.count))
  const scenarios = shape.scenarios.slice(0, 8)
  const maxScenario = Math.max(1, ...scenarios.map((s) => s.count))
  const replyShare = shape.messagesKept ? shape.replies / shape.messagesKept : 0
  return (
    <div className="shape">
      <div className="shape-stats">
        <Stat n={shape.messagesKept} label="of your messages kept" accent />
        {!!shape.inbound && <Stat n={shape.inbound} label="written to you" />}
        <Stat n={Math.round(replyShare * 100)} label="% replies" />
        <Stat n={shape.medianWords != null ? Math.round(shape.medianWords) : null} label="words, typical message" />
        <Stat n={shape.contractionRate != null ? Math.round(shape.contractionRate * 100) : null} label="% contractions" />
      </div>

      {shape.months.length > 0 && (
        <div className="shape-block">
          <h3>When you wrote</h3>
          <div className="months" role="img" aria-label="Messages per month">
            {shape.months.map((m) => (
              <div key={m.month} className="month" title={`${m.month}: ${m.count}`}>
                <span className="month-bar" style={{ height: `${Math.max(4, (m.count / maxMonth) * 100)}%` }} />
                <span className="month-label">{m.month.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {scenarios.length > 0 && (
        <div className="shape-block">
          <h3>Who you write to, and why</h3>
          <ul className="scen-bars">
            {scenarios.map((s) => (
              <li key={`${s.audience}/${s.intent}`}>
                <span className="scen-label" title={scenarioLabel(`email/${s.audience}/${s.intent}`)}>{scenarioLabel(`email/${s.audience}/${s.intent}`).replace(/^Email · /, '')}</span>
                <span className="scen-bar"><span style={{ width: `${(s.count / maxScenario) * 100}%` }} /></span>
                <span className="scen-n">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(shape.greetings.length > 0 || shape.closings.length > 0) && (
        <div className="shape-block two">
          <div>
            <h3>How you open</h3>
            <ul className="conv">{shape.greetings.map((g) => <li key={g.text}><span>{g.text}</span><em>{Math.round(g.rate * 100)}%</em></li>)}</ul>
          </div>
          <div>
            <h3>How you close</h3>
            <ul className="conv">{shape.closings.map((g) => <li key={g.text}><span>{g.text}</span><em>{Math.round(g.rate * 100)}%</em></li>)}</ul>
          </div>
        </div>
      )}

      {shape.profiles.length > 0 && (
        <div className="shape-block">
          <h3>Scenario profiles</h3>
          <ul className="profiles">
            {shape.profiles.map((p) => (
              <li key={p.scenario} className={p.selfCheck != null ? 'checked' : ''}>
                <span className="prof-name">{scenarioLabel(p.scenario).replace(/^Email · /, '')}</span>
                <span className="prof-meta">{p.messageCount} msgs · {p.exemplars} examples{p.described ? ' · described' : ''}</span>
                {p.selfCheck != null && <span className="prof-score" style={{ ['--score' as string]: p.selfCheck }}><strong>{Math.round(p.selfCheck * 100)}</strong></span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Stat({ n, label, accent }: { n: number | null; label: string; accent?: boolean }) {
  return (
    <div className={`stat ${accent ? 'accent' : ''}`}>
      <strong>{n == null ? '—' : n.toLocaleString()}</strong>
      <span className="muted">{label}</span>
    </div>
  )
}

function LiveLog({ state }: { state: LiveJob }) {
  const ref = useRef<HTMLOListElement>(null)
  const lines = useMemo(() => state.log.slice(-40), [state.log])
  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight }) }, [lines.length])
  if (lines.length === 0) return null
  return (
    <details className="card log-card" open={!isTerminal(state.job)}>
      <summary>Receipts <span className="muted">{state.log.length} lines</span></summary>
      <ol ref={ref} className="log">
        {lines.map((l, i) => (
          <li key={i} className={l.level}>
            <time>{new Date(l.at).toLocaleTimeString('en-US', { hour12: false })}</time>
            <span className="log-step">{l.stepId.replace('voice-', '')}</span>
            <span>{l.message}</span>
          </li>
        ))}
      </ol>
    </details>
  )
}

interface ScanShape {
  done: number
  target: number
  batches: number
  relationships: Record<string, number>
  kinds: Record<string, number>
  topCues: string[]
}

function isScanShape(x: unknown): x is ScanShape {
  return !!x && typeof x === 'object' && 'batches' in (x as object) && 'relationships' in (x as object)
}

function ScanPanel({ shape }: { shape: ScanShape }) {
  const rel = Object.entries(shape.relationships).sort((a, b) => b[1] - a[1])
  const kinds = Object.entries(shape.kinds).sort((a, b) => b[1] - a[1])
  const max = Math.max(1, ...rel.map(([, n]) => n))
  return (
    <div className="shape">
      <div className="shape-stats">
        <Stat n={shape.done} label="messages sorted" accent />
        <Stat n={shape.batches} label="batches, one session each" />
        <Stat n={shape.kinds['message'] ?? 0} label="carry your voice" />
        <Stat n={shape.done - (shape.kinds['message'] ?? 0)} label="automated, receipts, pastes" />
      </div>
      <div className="shape-block">
        <h3>Who you write to</h3>
        <ul className="scen-bars">
          {rel.map(([k, n]) => (
            <li key={k}>
              <span className="scen-label">{k}</span>
              <span className="scen-bar"><span style={{ width: `${(n / max) * 100}%` }} /></span>
              <span className="scen-n">{n}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="shape-block two">
        <div>
          <h3>Kinds of mail</h3>
          <ul className="conv">{kinds.map(([k, n]) => <li key={k}><span>{k}</span><em>{n}</em></li>)}</ul>
        </div>
        <div>
          <h3>Cues it is learning</h3>
          <ul className="conv">{shape.topCues.map((c) => <li key={c}><span>{c}</span></li>)}</ul>
        </div>
      </div>
    </div>
  )
}

interface DealShape {
  threadsRead: number
  threadsTotal: number
  batches: number
  stages: Record<string, number>
  companies: number
  amounts: number
  sowsSent: number
  latest: string[]
}

function isDealShape(x: unknown): x is DealShape {
  return !!x && typeof x === 'object' && 'threadsRead' in (x as object) && 'stages' in (x as object)
}

function DealPanel({ shape }: { shape: DealShape }) {
  const stages = Object.entries(shape.stages).filter(([k]) => k !== 'none').sort((a, b) => b[1] - a[1])
  const max = Math.max(1, ...stages.map(([, n]) => n))
  return (
    <div className="shape">
      <div className="shape-stats">
        <Stat n={shape.threadsRead} label="threads read" accent />
        <Stat n={stages.reduce((n, [, v]) => n + v, 0)} label="carry a pursuit" />
        <Stat n={shape.amounts} label="dollar amounts found" />
        <Stat n={shape.sowsSent} label="SOWs sent" />
      </div>
      <div className="shape-block">
        <h3>By stage</h3>
        <ul className="scen-bars">
          {stages.map(([k, n]) => (
            <li key={k}>
              <span className="scen-label">{k}</span>
              <span className="scen-bar"><span style={{ width: `${(n / max) * 100}%` }} /></span>
              <span className="scen-n">{n}</span>
            </li>
          ))}
        </ul>
      </div>
      {shape.latest.length > 0 && (
        <div className="shape-block">
          <h3>Latest found</h3>
          <ul className="conv">{shape.latest.map((l) => <li key={l}><span>{l}</span></li>)}</ul>
        </div>
      )}
    </div>
  )
}

// The calendar-sync job's shape: events read → meetings landed → resolved to accounts and contacts → transcripts tied.
// The shape serializer omits zero-valued counts, so every field is optional on the wire; default them.
interface CalendarShape { events?: number; landed?: number; withAccount?: number; withContacts?: number; upcoming?: number; linked?: number; nextUp?: string[] }
function isCalendarShape(x: unknown): x is CalendarShape {
  return !!x && typeof x === 'object' && ('events' in (x as object) || 'landed' in (x as object) || 'nextUp' in (x as object))
}
function CalendarPanel({ shape }: { shape: CalendarShape }) {
  const n = (v?: number) => (v ?? 0).toLocaleString()
  const next = shape.nextUp ?? []
  return (
    <div className="calendar-panel">
      <div className="deal-totals">
        <div className="stat accent"><strong>{n(shape.landed)}</strong><span className="muted">of {n(shape.events)} events landed</span></div>
        <div className="stat"><strong>{n(shape.upcoming)}</strong><span className="muted">ahead of you</span></div>
        <div className="stat"><strong>{n(shape.withContacts)}</strong><span className="muted">with a known contact</span></div>
        <div className="stat"><strong>{n(shape.withAccount)}</strong><span className="muted">resolved to an account</span></div>
        <div className="stat"><strong>{n(shape.linked)}</strong><span className="muted">transcripts tied</span></div>
      </div>
      {next.length > 0 && (
        <>
          <p className="muted small">Next up:</p>
          <ul className="plain">{next.map((x) => <li key={x}>{x}</li>)}</ul>
        </>
      )}
    </div>
  )
}

// The transcript-extract job's shape: transcripts read, and the ledger lines found so far.
interface LedgerShape { transcripts?: number; done?: number; requirements?: number; takeaways?: number; ours?: number; theirs?: number; withDue?: number; latest?: string[] }
function isLedgerShape(x: unknown): x is LedgerShape {
  return !!x && typeof x === 'object' && ('requirements' in (x as object) || 'takeaways' in (x as object)) && 'transcripts' in (x as object)
}
function LedgerPanel({ shape }: { shape: LedgerShape }) {
  const n = (v?: number) => (v ?? 0).toLocaleString()
  const latest = shape.latest ?? []
  return (
    <div className="shape">
      <div className="shape-stats">
        <Stat n={shape.done ?? 0} label={`of ${n(shape.transcripts)} transcripts read`} accent />
        <Stat n={shape.requirements ?? 0} label="requirements" />
        <Stat n={shape.takeaways ?? 0} label={`takeaways · ${n(shape.ours)} ours, ${n(shape.theirs)} theirs`} />
        <Stat n={shape.withDue ?? 0} label="with a promised date" />
      </div>
      {latest.length > 0 && (
        <div className="shape-block">
          <h3>Latest read</h3>
          <ul className="conv">{latest.map((l) => <li key={l}><span>{l}</span></li>)}</ul>
        </div>
      )}
    </div>
  )
}
// The lead-research job's shape: the evidence counts the note stands on, then the note's outline once written.
function parseResearchShape(json: string): LeadResearchShape | null {
  try { const x = JSON.parse(json); return x && typeof x === 'object' && ('companyName' in x || 'groundedOn' in x) ? (x as LeadResearchShape) : null } catch { return null }
}
function ResearchPanel({ shape }: { shape: LeadResearchShape }) {
  const n = (v?: number) => (v ?? 0).toLocaleString()
  return (
    <div className="shape">
      <div className="shape-stats">
        <Stat n={shape.messages ?? 0} label={`messages to ${shape.companyName ?? 'the company'}`} accent />
        <Stat n={shape.people ?? 0} label="people on file" />
        <Stat n={shape.signals ?? 0} label="signals read" />
        <Stat n={shape.meetings ?? 0} label="meetings on the calendar" />
      </div>
      {(shape.groundedOn?.length ?? 0) > 0 && (
        <div className="shape-block">
          <h3>Grounded on</h3>
          <ul className="conv">{shape.groundedOn!.map((g) => <li key={g}><span>{g}</span></li>)}</ul>
        </div>
      )}
      {shape.written && (
        <div className="shape-block">
          <h3>The note</h3>
          <ul className="conv">
            <li><span>sector</span><em>{shape.sector ?? 'not on file'}</em></li>
            <li><span>likely needs</span><em>{n(shape.needs)}</em></li>
            <li><span>competitors named</span><em>{n(shape.competitors)}</em></li>
            <li><span>risks</span><em>{n(shape.risks)}</em></li>
          </ul>
        </div>
      )}
    </div>
  )
}
/** The transcript a transcript-extract job was started for (null when it covers every unread one). */
function transcriptOf(job: Job): string | null {
  if (!job.requestJson) return null
  try { const r = JSON.parse(job.requestJson) as { transcriptId?: string | null }; return r.transcriptId ?? null } catch { return null }
}
// The weekly-write job's shape: facts in, facts cited, words out, and the headline once there is one.
interface WeeklyShape { weekStart?: string; facts?: number; cited?: number; words?: number; modelWritten?: boolean; headline?: string | null }
function parseWeeklyShape(json: string): WeeklyShape | null {
  try { const x = JSON.parse(json); return x && typeof x === 'object' && 'facts' in x ? (x as WeeklyShape) : null } catch { return null }
}
function WeeklyPanel({ shape }: { shape: WeeklyShape }) {
  const n = (v?: number) => (v ?? 0).toLocaleString()
  return (
    <div className="calendar-panel">
      <div className="deal-totals">
        <div className="stat accent"><strong>{n(shape.facts)}</strong><span className="muted">facts on file this week</span></div>
        <div className="stat"><strong>{n(shape.cited)}</strong><span className="muted">cited by the prose</span></div>
        <div className="stat"><strong>{n(shape.words)}</strong><span className="muted">words</span></div>
        <div className="stat"><strong>{shape.modelWritten ? 'model' : shape.words ? 'facts' : '—'}</strong><span className="muted">{shape.modelWritten ? 'written by the model' : shape.words ? 'laid out without a model' : 'not written yet'}</span></div>
      </div>
      {shape.headline && <blockquote className="exemplar"><pre>{shape.headline}</pre></blockquote>}
    </div>
  )
}

// The photo-enrich job's shape: candidates looked up, photos found and not, then the coverage once landed.
interface PhotoShape { candidates?: number; checked?: number; found?: number; missing?: number; landed?: number; people?: number; withPhoto?: number; bySource?: Record<string, number>; latest?: string[] }
function parsePhotoShape(json: string): PhotoShape | null {
  try { const x = JSON.parse(json); return x && typeof x === 'object' && ('candidates' in x || 'withPhoto' in x || 'checked' in x) ? (x as PhotoShape) : null } catch { return null }
}
function PhotoPanel({ shape }: { shape: PhotoShape }) {
  const n = (v?: number) => (v ?? 0).toLocaleString()
  const sources = Object.entries(shape.bySource ?? {}).sort((a, b) => b[1] - a[1])
  const landed = (shape.people ?? 0) > 0
  return (
    <div className="calendar-panel">
      <div className="deal-totals">
        {landed ? (
          <>
            <div className="stat accent"><strong>{n(shape.withPhoto)}</strong><span className="muted">of {n(shape.people)} contacts have a photo</span></div>
            <div className="stat"><strong>{n(shape.landed)}</strong><span className="muted">landed under Scout this run</span></div>
            {sources.map(([k, v]) => <div key={k} className="stat"><strong>{v.toLocaleString()}</strong><span className="muted">from {k}</span></div>)}
          </>
        ) : (
          <>
            <div className="stat accent"><strong>{n(shape.checked)}</strong><span className="muted">of {n(shape.candidates)} contacts looked up</span></div>
            <div className="stat"><strong>{n(shape.found)}</strong><span className="muted">have a published photo</span></div>
            <div className="stat"><strong>{n(shape.missing)}</strong><span className="muted">have none published</span></div>
          </>
        )}
      </div>
      {(shape.latest?.length ?? 0) > 0 && (
        <>
          <p className="muted small">Found so far:</p>
          <ul className="plain">{shape.latest!.map((x) => <li key={x}>{x}</li>)}</ul>
        </>
      )}
    </div>
  )
}

// The threat-watch job's shape: rivals on the register, what was read, what was found, what was repaired.
interface ThreatShape { rivals?: number; meetingsRead?: number; threadsRead?: number; threats?: number; judged?: number; reclassified?: number; latest?: string[] }
function parseThreatShape(json: string): ThreatShape | null {
  try { const x = JSON.parse(json); return x && typeof x === 'object' && ('rivals' in x || 'threats' in x) ? (x as ThreatShape) : null } catch { return null }
}
function ThreatPanel({ shape }: { shape: ThreatShape }) {
  const n = (v?: number) => (v ?? 0).toLocaleString()
  const found = shape.latest ?? []
  return (
    <div className="calendar-panel">
      <div className="deal-totals">
        <div className={`stat ${(shape.threats ?? 0) > 0 ? 'accent' : ''}`}><strong>{n(shape.threats)}</strong><span className="muted">threat{(shape.threats ?? 0) === 1 ? '' : 's'} found</span></div>
        <div className="stat"><strong>{n(shape.rivals)}</strong><span className="muted">rivals on the register</span></div>
        <div className="stat"><strong>{n(shape.meetingsRead)}</strong><span className="muted">meetings read</span></div>
        <div className="stat"><strong>{n(shape.threadsRead)}</strong><span className="muted">threads read</span></div>
        <div className="stat"><strong>{n(shape.reclassified)}</strong><span className="muted">taken out of the pipeline</span></div>
      </div>
      {found.length > 0 && (
        <>
          <p className="muted small">Found so far:</p>
          <ul className="plain">{found.map((x) => <li key={x}>{x}</li>)}</ul>
        </>
      )}
    </div>
  )
}

// The contact-onboard job's shape: what the file held, what survived the clean, and what the match
// and the flag made of it. Nothing it counts has landed — a batch is a proposal until it is committed.
function parseOnboardShape(json: string): OnboardShape | null {
  try { const x = JSON.parse(json); return x && typeof x === 'object' && 'batchId' in x && 'rows' in x ? (x as OnboardShape) : null } catch { return null }
}
function OnboardPanel({ shape }: { shape: OnboardShape }) {
  const n = (v?: number) => (v ?? 0).toLocaleString()
  const latest = shape.latest ?? []
  return (
    <div className="calendar-panel">
      <div className="deal-totals">
        <div className="stat accent"><strong>{n(shape.cleaned)}</strong><span className="muted">of {n(shape.rows)} rows staged</span></div>
        <div className="stat"><strong>{n(shape.flagged)}</strong><span className="muted">already know you</span></div>
        <div className="stat"><strong>{n(shape.new)}</strong><span className="muted">new · {n(shape.merge)} merge</span></div>
        <div className="stat"><strong>{n(shape.conflict)}</strong><span className="muted">conflicted · {n(shape.skip)} identical</span></div>
        <div className="stat"><strong>{n(shape.duplicates)}</strong><span className="muted">duplicates folded in</span></div>
        <div className="stat"><strong>{n(shape.publicMail)}</strong><span className="muted">on a personal address</span></div>
      </div>
      {latest.length > 0 && (
        <>
          <p className="muted small">So far:</p>
          <ul className="plain">{latest.map((x) => <li key={x}>{x}</li>)}</ul>
        </>
      )}
    </div>
  )
}

// The one-line completion summary, per kind. shapeJson is cast loosely, so read only via the right guard.
function completionLine(job: Job, shape: VoiceShape | null, gaps: boolean): string {
  const g = gaps ? ` ${job.warnings} gap${job.warnings === 1 ? '' : 's'} left; see what did not happen above.` : ''
  const s: any = job.shapeJson ? (() => { try { return JSON.parse(job.shapeJson!) } catch { return null } })() : null
  const done = job.steps.reduce((n, x) => Math.max(n, x.done), 0)
  switch (job.kind) {
    case 'transcript-extract': return `${(s?.done ?? done).toLocaleString()} transcript${(s?.done ?? done) === 1 ? '' : 's'} read: ${(s?.requirements ?? 0).toLocaleString()} requirements and ${(s?.takeaways ?? 0).toLocaleString()} takeaways on the ledger (${(s?.ours ?? 0).toLocaleString()} we owe, ${(s?.theirs ?? 0).toLocaleString()} they owe).${g}`
    case 'weekly-write': return s?.modelWritten ? `The week is written: ${(s?.words ?? 0).toLocaleString()} words citing ${(s?.cited ?? 0).toLocaleString()} of ${(s?.facts ?? 0).toLocaleString()} facts on file.` : `The week is laid out from ${(s?.facts ?? 0).toLocaleString()} facts on file, without a model.${g}`
    case 'notes-sync': return `${(s?.pages ?? done).toLocaleString()} pages in ${(s?.notebooks ?? 0).toLocaleString()} notebook${(s?.notebooks ?? 0) === 1 ? '' : 's'}; ${(s?.new ?? 0).toLocaleString()} new, ${(s?.changed ?? 0).toLocaleString()} changed; tied to ${(s?.people ?? 0).toLocaleString()} people and ${(s?.accounts ?? 0).toLocaleString()} accounts.${g}`
    case 'calendar-sync': return `${(s?.landed ?? done).toLocaleString()} events read; ${(s?.withContacts ?? 0).toLocaleString()} meetings have a known contact and ${(s?.linked ?? 0).toLocaleString()} transcripts are tied to their meetings.${g}`
    case 'deal-scan': return `${(s?.threadsRead ?? done).toLocaleString()} threads read into your pipeline.${g}`
    case 'voice-scan': return `${(s?.scanned ?? done).toLocaleString()} messages sorted by audience and intent.${g}`
    case 'contact-watch': return `Your relationships are re-weighed from your mail.${g}`
    case 'threat-watch': return (s?.threats ?? 0) > 0
      ? `${(s?.threats ?? 0).toLocaleString()} competitor threat${(s?.threats ?? 0) === 1 ? '' : 's'} at your accounts, from ${(s?.rivals ?? 0).toLocaleString()} rival${(s?.rivals ?? 0) === 1 ? '' : 's'} on the register${(s?.reclassified ?? 0) > 0 ? `; ${(s?.reclassified ?? 0).toLocaleString()} pursuit${(s?.reclassified ?? 0) === 1 ? '' : 's'} that were really someone else's evaluation left the pipeline` : ''}.${g}`
      : `No rival was found at any of your accounts. ${(s?.rivals ?? 0).toLocaleString()} on the register; add more under the deployment's competitors.${g}`
    case 'photo-enrich': return `Photos on ${(s?.withPhoto ?? 0).toLocaleString()} of ${(s?.people ?? 0).toLocaleString()} contacts; ${(s?.landed ?? 0).toLocaleString()} landed this run. LinkedIn and Microsoft 365 photos come next.${g}`
    case 'priority-contacts': return `${(s?.cleaned ?? 0).toLocaleString()} rows of your workbook staged from ${s?.fileName ?? 'the file'} with your bands on them; ${(s?.new ?? 0).toLocaleString()} not on file, ${(s?.merge ?? 0).toLocaleString()} matched, ${(s?.conflict ?? 0).toLocaleString()} in conflict. Nothing has landed — read the reconciliation and commit.${g}`
    case 'contact-onboard': return (s?.flagged ?? 0) > 0
      ? `${(s?.cleaned ?? 0).toLocaleString()} contacts staged from ${s?.fileName ?? 'the file'}; ${(s?.flagged ?? 0).toLocaleString()} of them already have a two-way relationship with the firm. Nothing has landed — review it and commit.${g}`
      : `${(s?.cleaned ?? 0).toLocaleString()} contacts staged from ${s?.fileName ?? 'the file'}${(s?.duplicates ?? 0) > 0 ? `, ${(s?.duplicates ?? 0).toLocaleString()} duplicate(s) folded in` : ''}. None of them has answered you or met you. Nothing has landed — review it and commit.${g}`
    case 'transcript-import': return `Meeting transcripts pulled into their project context.${g}`
    case 'methodology-ingest': return `The methodology is laid out as a workflow.${g}`
    case 'lead-research': return `The research note for ${s?.companyName ?? 'the company'} is on file, grounded on ${(s?.messages ?? 0).toLocaleString()} messages, ${(s?.people ?? 0).toLocaleString()} people and ${(s?.signals ?? 0).toLocaleString()} signals.${g}`
    default: return `Your profile is built from ${(shape?.messagesKept ?? job.steps[0]?.done ?? 0).toLocaleString()} of your own messages${shape?.selfCheck != null ? `, and it checks out at ${Math.round(shape.selfCheck * 100)}% like you` : ''}.${g}`
  }
}
